import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import MatchBadge from "@/components/MatchBadge";

// Fix default marker icons in Webpack bundles (Leaflet expects specific URLs)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Custom colored icon (moss green) so pins feel on-brand
const brandIcon = L.divIcon({
  className: "sadhya-map-pin",
  html: `<div style="width:26px;height:26px;border-radius:50%;background:#4B6858;border:3px solid #F5EFE6;box-shadow:0 2px 6px rgba(0,0,0,0.25);"></div>`,
  iconSize: [26, 26],
  iconAnchor: [13, 26],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return undefined;
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 8, animate: false });
    return () => {
      try { map.stop(); } catch (e) { console.warn("Map stop failed on unmount:", e); }
    };
  }, [map, points]);
  return null;
}

export default function MapView({ experiences }) {
  const pinnable = useMemo(
    () => experiences.filter((e) => typeof e.lat === "number" && typeof e.lng === "number"),
    [experiences]
  );
  const missing = experiences.length - pinnable.length;
  const center = pinnable.length ? [pinnable[0].lat, pinnable[0].lng] : [20.5937, 78.9629]; // India centroid
  const points = pinnable.map((e) => [e.lat, e.lng]);

  return (
    <div data-testid="discover-map" className="relative">
      <div className="rounded-3xl overflow-hidden border border-subtle shadow-sm bg-white">
        <MapContainer
          center={center}
          zoom={5}
          style={{ height: "70vh", width: "100%" }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds points={points} />
          {pinnable.map((e) => (
            <Marker key={e.id} position={[e.lat, e.lng]} icon={brandIcon}>
              <Popup maxWidth={260}>
                <Link to={`/experience/${e.id}`} className="block no-underline">
                  {e.images?.[0] && (
                    <img src={e.images[0]} alt="" className="w-full h-28 object-cover rounded-lg mb-2" />
                  )}
                  <div className="font-serif text-base leading-tight" style={{ color: "#2A2A2A" }}>{e.title}</div>
                  <div className="text-xs text-muted2 mt-0.5" style={{ color: "#7A7A7A" }}>
                    {e.location}{e.country ? `, ${e.country}` : ""}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    {typeof e.match_score === "number" && <MatchBadge score={e.match_score} />}
                    <span className="text-xs" style={{ color: "#2A2A2A" }}>from ${Math.round(e.price_usd || 0)}</span>
                  </div>
                </Link>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      {missing > 0 && (
        <div className="mt-3 text-xs text-muted2" data-testid="map-missing-count">
          {missing} experience{missing === 1 ? "" : "s"} hidden — location not yet geocoded.
        </div>
      )}
    </div>
  );
}
